<!-- src/views/legal/About.vue -->
<!-- TEMPLATE — have counsel review before production reliance. -->

<script setup lang="ts">
  import { RouterLink } from 'vue-router';

  // Fixed publication date. Do NOT compute at runtime (no Date.now) so the
  // "Last updated" string is stable and auditable across renders/deploys.
  const lastUpdated = 'July 6, 2026';

  const h2 = 'mt-12 mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100';
  const p = 'mb-4 text-base leading-7 text-gray-600 dark:text-gray-300';
  const ul = 'mb-4 ml-5 list-disc space-y-2 text-base leading-7 text-gray-600 dark:text-gray-300';
  const a =
    'font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300';
</script>

<template>
  <div class="container mx-auto max-w-3xl px-4 py-8">
    <article aria-labelledby="about-title">
      <header>
        <h1
          id="about-title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          About BurnLink
        </h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
      </header>

      <p :class="[p, 'mt-8']">
        BurnLink is a privacy-first way to share sensitive information — passwords, API keys, private
        notes — through links that self-destruct after a single view or when they expire. Instead of
        leaving secrets sitting in chat histories and inboxes forever, you send a link that can be
        opened exactly once and is then permanently destroyed.
      </p>

      <h2 :class="h2">What we believe</h2>
      <p :class="p">
        The safest data is the data no one keeps. BurnLink is designed around collecting as little
        as possible and deleting aggressively: secrets are encrypted so that we cannot read them,
        they are revealed at most once, and they are gone the moment they are read or expire.
        Automated link previews, security scanners, or whoever opens the link first can consume
        it before your intended recipient does — share one-time links thoughtfully. You
        should not have to trust us with the contents of your secrets — and by design, you do not.
      </p>

      <h2 :class="h2">Built on Onetime Secret</h2>
      <p :class="p">
        BurnLink is based on the MIT-licensed Onetime Secret project. BurnLink's hosted
        service, branding, and product changes are operated separately. We are grateful to
        the Onetime Secret maintainers and community for the foundation their work provides.
      </p>

      <h2 :class="h2">How it runs</h2>
      <ul :class="ul">
        <li>Self-hosted and operated by the BurnLink team, not a third-party SaaS reseller.</li>
        <li>Hosted on Fly.io and served through the Cloudflare edge network.</li>
        <li>Encryption at rest, exactly-once reveal, and atomic delete on read.</li>
        <li>Minimal data collection and short retention by default.</li>
      </ul>
      <p :class="p">
        For the technical details, see our
        <RouterLink :class="a" to="/security">Security</RouterLink> page. For how we handle data,
        see our <RouterLink :class="a" to="/privacy">Privacy Policy</RouterLink>.
      </p>

      <h2 :class="h2">Contact</h2>
      <p :class="p">
        General questions: <a :class="a" href="mailto:support@burnlink.app">support@burnlink.app</a>.
        Privacy: <a :class="a" href="mailto:privacy@burnlink.app">privacy@burnlink.app</a>.
        Security: <a :class="a" href="mailto:security@burnlink.app">security@burnlink.app</a>.
      </p>
    </article>
  </div>
</template>
